import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { billingApi, messagingApi } from '@/api/ops';
import type { ApiKey } from '@/api/opsTypes';
import { PERMISSIONS } from '@/api/types';
import { hasPermission } from '@/auth/session';
import { Select, Segmented } from '@/components/Select';
import { StatusChip } from '@/components/ops/Badges';
import { CheckboxField, ChoiceChips, ReasonField, TextAreaField, TextField, reasonOk, splitList, toggleIn } from '@/components/ops/Forms';
import { GatewayForm, GatewaysTable, gatewayQueryKey } from '@/components/ops/Gateways';
import { Section } from '@/components/ops/Layout';
import { ConfirmDialog, Modal, RevealOnce } from '@/components/ops/Modal';
import { RequirePermission } from '@/components/ops/PermissionGate';
import { MutationError, QueryState } from '@/components/ops/Problem';
import { formatTimestamp, localInputToIso, shortId } from '@/ops/format';
import { LIVE } from '@/ops/live';

/** Every scope a key may carry (CONTRACTS §3.3). */
export const ALL_SCOPES = [
  'sms:send', 'sms:read', 'otp:send', 'otp:verify', 'sms:templates', 'sms:campaigns', 'sms:suppressions',
  'webhooks:manage', 'accounts:manage', 'billing:read', 'billing:purchase', 'payments:intents', 'payments:refunds',
] as const;

/** A publishable (browser) key may hold ONLY these. */
export const PUBLISHABLE_SCOPES = ['otp:send', 'otp:verify', 'sms:send'] as const;

/** Secret keys default to every scope except `accounts:manage` (parents only). */
export const SECRET_DEFAULT_SCOPES = ALL_SCOPES.filter((s) => s !== 'accounts:manage');

const EXT_WEBHOOK_EVENTS = ['payment.succeeded', 'payment.failed', 'payment.refunded'];

/** Keeps a scope set legal for the key kind — switching to publishable drops what it may not hold. */
export function scopesForKind(kind: 'secret' | 'publishable', scopes: string[]): string[] {
  if (kind === 'secret') return scopes;
  return scopes.filter((s) => (PUBLISHABLE_SCOPES as readonly string[]).includes(s));
}

/** `scheme://host[:port]` exactly — what the gateway compares an Origin header against. */
export function originError(origin: string): string | null {
  try {
    const url = new URL(origin);
    if (url.protocol !== 'https:' && url.protocol !== 'http:') return `${origin}: http(s) only.`;
    if (url.origin !== origin.replace(/\/$/, '')) return `${origin}: an origin is scheme://host[:port] with no path.`;
    return null;
  } catch {
    return `${origin}: not an origin (https://shop.example.com).`;
  }
}

const keysQuery = (env: string, tenantId: string) => ['ops', 'billing', env, 'api-keys', tenantId] as const;

/**
 * The external account's credentials (D-CP16): API keys issued / rotated / revoked by billing (the
 * plaintext comes back ONCE and is never stored — not in the console, not in the query cache), the BYO
 * payment gateways its customers pay through (BL8), and its signed ext-webhook endpoint.
 */
export function ApiKeysTab({ env, tenantId }: { env: string; tenantId: string }) {
  return (
    <RequirePermission permission={PERMISSIONS.billingRead} what="API keys">
      <div className="stack">
        <ApiKeysSection env={env} tenantId={tenantId} />
        <ByoGatewaysSection env={env} tenantId={tenantId} />
        <ExtWebhookSection env={env} tenantId={tenantId} />
      </div>
    </RequirePermission>
  );
}

function ApiKeysSection({ env, tenantId }: { env: string; tenantId: string }) {
  const queryClient = useQueryClient();
  const canManage = hasPermission(PERMISSIONS.billingManage);
  const keys = useQuery({ queryKey: keysQuery(env, tenantId), queryFn: () => billingApi.apiKeys(env, tenantId), ...LIVE });
  const [creating, setCreating] = useState(false);
  const [revealed, setRevealed] = useState<{ key: string; display: string; kind: string; rotated: boolean } | null>(null);
  const [rotating, setRotating] = useState<ApiKey | null>(null);
  const [revoking, setRevoking] = useState<ApiKey | null>(null);
  const [reason, setReason] = useState('');

  // gcTime 0 + reset(): the plaintext moves into `revealed` and nothing else keeps it.
  const rotate = useMutation({ mutationFn: (id: string) => billingApi.rotateApiKey(env, id), gcTime: 0 });
  const revoke = useMutation({
    mutationFn: () => billingApi.revokeApiKey(env, revoking!.key_id, reason.trim()),
    onSuccess: () => {
      setRevoking(null);
      setReason('');
      queryClient.invalidateQueries({ queryKey: keysQuery(env, tenantId) });
    },
  });

  return (
    <Section title="API keys" meta="verified at the gateway; the plaintext is shown once"
      actions={canManage && <button type="button" className="btn btn-ghost btn-sm" onClick={() => setCreating(true)}>Create key</button>} flush>
      <QueryState query={keys} service="billing">
        {(data) => data.api_keys.length === 0 ? (
          <div className="empty-state t-body">No API keys for this account.</div>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr><th>Key</th><th>Kind</th><th>Scopes</th><th>Origins · limit</th><th>Status</th><th>Used</th>{canManage && <th aria-label="Actions" />}</tr>
              </thead>
              <tbody>
                {data.api_keys.map((k) => (
                  <tr key={k.key_id} className={k.status === 'active' ? undefined : 'row-muted'}>
                    <td>
                      <div style={{ fontWeight: 600 }}>{k.name}</div>
                      <div className="t-caption mono muted">{k.display}</div>
                      {k.account_id && <div className="t-caption muted">bound to account <span className="mono">{shortId(k.account_id)}</span></div>}
                    </td>
                    <td>
                      <StatusChip status={k.kind} tone={k.kind === 'publishable' ? 'info' : 'muted'} />
                      {k.sandbox && <StatusChip status="sandbox" tone="warning" />}
                    </td>
                    <td className="t-caption" style={{ maxWidth: 260 }}>{k.scopes.join(' ')}</td>
                    <td className="t-caption">
                      {k.allowed_origins.length ? k.allowed_origins.map((o) => <div key={o} className="mono">{o}</div>) : <span className="subtle">any (server-to-server)</span>}
                      <div className="muted">{k.rate_limit_per_minute}/min</div>
                    </td>
                    <td>
                      <StatusChip status={k.status} />
                      {k.revoked_reason && <div className="t-caption muted">{k.revoked_reason}</div>}
                      {k.expires_at && <div className="t-caption muted">expires {formatTimestamp(k.expires_at)}</div>}
                    </td>
                    <td className="t-caption">{k.last_used_at ? formatTimestamp(k.last_used_at) : <span className="subtle">never</span>}<div className="muted">created {formatTimestamp(k.created_at)}</div></td>
                    {canManage && (
                      <td>
                        {k.status === 'active' && (
                          <div className="row">
                            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setRotating(k)}>Rotate</button>
                            <button type="button" className="btn btn-ghost btn-sm is-danger" onClick={() => setRevoking(k)}>Revoke</button>
                          </div>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </QueryState>

      {creating && (
        <CreateKeyForm env={env} tenantId={tenantId} onClose={() => setCreating(false)}
          onCreated={(secret) => {
            setCreating(false);
            setRevealed({ key: secret.key, display: secret.api_key.display, kind: secret.api_key.kind, rotated: false });
            queryClient.invalidateQueries({ queryKey: keysQuery(env, tenantId) });
          }} />
      )}

      {rotating && (
        <ConfirmDialog
          title={`Rotate “${rotating.name}”?`}
          confirmLabel="Rotate key"
          destructive
          pending={rotate.isPending}
          onConfirm={() => rotate.mutate(rotating.key_id, {
            onSuccess: (secret) => {
              const plaintext = secret.key;
              rotate.reset();
              setRotating(null);
              setRevealed({ key: plaintext, display: secret.api_key.display, kind: secret.api_key.kind, rotated: true });
              queryClient.invalidateQueries({ queryKey: keysQuery(env, tenantId) });
            },
          })}
          onCancel={() => { setRotating(null); rotate.reset(); }}
        >
          <p className="t-body" style={{ margin: 0 }}>
            A new key replaces <span className="mono">{rotating.display}</span>. The new plaintext is shown once; update the
            integration with it straight away.
          </p>
          <MutationError error={rotate.error} service="billing" />
        </ConfirmDialog>
      )}

      {revoking && (
        <ConfirmDialog
          title={`Revoke “${revoking.name}”?`}
          confirmLabel="Revoke key"
          destructive
          pending={revoke.isPending}
          confirmDisabled={!reasonOk(reason)}
          onConfirm={() => revoke.mutate()}
          onCancel={() => { setRevoking(null); setReason(''); revoke.reset(); }}
        >
          <p className="t-body" style={{ margin: 0 }}>
            Calls with <span className="mono">{revoking.display}</span> are refused at the gateway (401) as soon as its cache
            evicts the key. This cannot be undone — issue a new key instead.
          </p>
          <ReasonField value={reason} onChange={setReason} />
          <MutationError error={revoke.error} service="billing" />
        </ConfirmDialog>
      )}

      {revealed && (
        <RevealOnce title={revealed.rotated ? 'Rotated API key' : 'New API key'} what="API key" secret={revealed.key}
          onDismiss={() => setRevealed(null)}>
          <p className="t-caption muted" style={{ margin: 0 }}>
            {revealed.kind === 'publishable'
              ? 'Publishable key: safe for a browser, limited to otp:send / otp:verify / sms:send and to its allowed origins.'
              : 'Secret key: server-to-server only — never ship it to a browser or a mobile app.'}{' '}
            Sent as the <span className="mono">X-Api-Key</span> header. Later reads show only <span className="mono">{revealed.display}</span>.
          </p>
        </RevealOnce>
      )}
    </Section>
  );
}

function CreateKeyForm({
  env, tenantId, onClose, onCreated,
}: {
  env: string;
  tenantId: string;
  onClose: () => void;
  onCreated: (secret: { key: string; api_key: ApiKey }) => void;
}) {
  const [name, setName] = useState('');
  const [kind, setKind] = useState<'secret' | 'publishable'>('secret');
  const [scopes, setScopes] = useState<string[]>([...SECRET_DEFAULT_SCOPES]);
  const [origins, setOrigins] = useState('');
  const [rateLimit, setRateLimit] = useState('600');
  const [sandbox, setSandbox] = useState(false);
  const [accountId, setAccountId] = useState('');
  const [expiresAt, setExpiresAt] = useState('');

  // Child accounts to bind the key to (optional): only when the role can read messaging.
  const accounts = useQuery({
    queryKey: ['ops', 'messaging', env, 'accounts', tenantId],
    queryFn: () => messagingApi.accounts(env, tenantId),
    enabled: hasPermission(PERMISSIONS.messagingRead),
    ...LIVE,
  });
  const children = (accounts.data?.accounts ?? []).filter((a) => a.kind === 'child');

  const originList = splitList(origins);
  const originErrors = originList.map(originError).filter((e): e is string => e !== null);
  const rate = Number(rateLimit);
  const rateError = !Number.isInteger(rate) || rate < 1 ? 'A whole number of requests per minute.' : null;
  const publishableIssues =
    kind === 'publishable' && originList.length === 0 ? 'A publishable key needs at least one allowed origin.' : null;
  const valid = name.trim() !== '' && scopes.length > 0 && originErrors.length === 0 && !rateError && !publishableIssues;

  const changeKind = (next: 'secret' | 'publishable') => {
    setKind(next);
    setScopes(next === 'publishable' ? [...PUBLISHABLE_SCOPES] : [...SECRET_DEFAULT_SCOPES]);
    setRateLimit(next === 'publishable' ? '60' : '600');
  };

  const create = useMutation({
    mutationFn: () => billingApi.createApiKey(env, {
      tenant_id: tenantId,
      name: name.trim(),
      kind,
      scopes: scopesForKind(kind, scopes),
      allowed_origins: originList,
      rate_limit_per_minute: rate,
      sandbox,
      account_id: accountId || null,
      expires_at: localInputToIso(expiresAt),
    }),
    gcTime: 0,
  });

  const allowedScopes: readonly string[] = kind === 'publishable' ? PUBLISHABLE_SCOPES : ALL_SCOPES;

  return (
    <Modal title="Create an API key" size="lg" onClose={create.isPending ? undefined : onClose} dismissible={!create.isPending}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={create.isPending}>Cancel</button>
          <button type="button" className="btn btn-primary" disabled={!valid || create.isPending}
            onClick={() => create.mutate(undefined, {
              onSuccess: (secret) => {
                create.reset();
                onCreated(secret);
              },
            })}>
            {create.isPending ? 'Creating…' : 'Create key'}
          </button>
        </>
      }>
      <div className="form-grid">
        <TextField label="Name" value={name} onChange={setName} required placeholder="ordering-platform production" />
        <Segmented label="Kind" value={kind} onChange={changeKind}
          options={[{ value: 'secret', label: 'Secret (server)' }, { value: 'publishable', label: 'Publishable (browser)' }]} />
      </div>
      <ChoiceChips
        label="Scopes"
        options={ALL_SCOPES.map((s) => ({ value: s }))}
        selected={scopes}
        disabledOptions={ALL_SCOPES.filter((s) => !allowedScopes.includes(s))}
        onToggle={(s) => setScopes(toggleIn(scopes, s))}
        hint={kind === 'publishable'
          ? 'A publishable key may hold only otp:send, otp:verify and sms:send.'
          : 'accounts:manage is for parent accounts only (children via on_behalf_of).'}
      />
      <TextAreaField label={`Allowed origins${kind === 'publishable' ? ' (required)' : ''}`} value={origins} onChange={setOrigins} mono rows={2}
        placeholder="https://shop.example.com" error={publishableIssues ?? (originErrors[0] ?? null)}
        hint="One per line or comma separated — scheme://host[:port], compared exactly with the browser's Origin." />
      <div className="form-grid">
        <TextField label="Rate limit (per minute)" value={rateLimit} onChange={setRateLimit} type="number" min={1} error={rateError} />
        <TextField label="Expires (your local time)" value={expiresAt} onChange={setExpiresAt} type="datetime-local" hint="Empty = no expiry." />
        {children.length > 0 && (
          <Select label="Bind to account" value={accountId} onChange={setAccountId} placeholder="The parent (whole tenant)"
            options={children.map((c) => ({ value: c.account_id, label: c.name, hint: c.external_ref ?? undefined }))} />
        )}
      </div>
      <CheckboxField label="Sandbox" checked={sandbox} onChange={setSandbox} hint="Calls with this key are stamped X-Api-Sandbox: true by the gateway." />
      <MutationError error={create.error} service="billing" />
    </Modal>
  );
}

function ByoGatewaysSection({ env, tenantId }: { env: string; tenantId: string }) {
  const [creating, setCreating] = useState(false);
  const gateways = useQuery({
    queryKey: [...gatewayQueryKey(env), 'byo', tenantId],
    queryFn: () => billingApi.gateways(env, { owner_tenant_id: tenantId }),
    ...LIVE,
  });

  return (
    <Section title="BYO payment gateways" meta="this account's own merchant credentials — its customers pay through these only"
      actions={hasPermission(PERMISSIONS.billingManage) && (
        <button type="button" className="btn btn-ghost btn-sm" onClick={() => setCreating(true)}>Add BYO gateway</button>
      )} flush>
      <QueryState query={gateways} service="billing">
        {(data) => <GatewaysTable env={env} gateways={data.gateways} showOwner={false} />}
      </QueryState>
      {creating && <GatewayForm env={env} ownerTenantId={tenantId} onClose={() => setCreating(false)} />}
    </Section>
  );
}

function ExtWebhookSection({ env, tenantId }: { env: string; tenantId: string }) {
  const queryClient = useQueryClient();
  const canManage = hasPermission(PERMISSIONS.billingManage);
  const webhook = useQuery({ queryKey: ['ops', 'billing', env, 'ext-webhook', tenantId], queryFn: () => billingApi.extWebhook(env, tenantId), ...LIVE });
  const [editing, setEditing] = useState(false);
  const [url, setUrl] = useState('');
  const [events, setEvents] = useState<string[]>([...EXT_WEBHOOK_EVENTS]);
  const [enabled, setEnabled] = useState(true);
  const [rotateSecret, setRotateSecret] = useState(false);
  const [reason, setReason] = useState('');
  const [secret, setSecret] = useState<string | null>(null);

  const startEdit = () => {
    const endpoint = webhook.data?.endpoint;
    setUrl(endpoint?.url ?? '');
    setEvents(endpoint?.events ?? [...EXT_WEBHOOK_EVENTS]);
    setEnabled(endpoint?.enabled ?? true);
    setRotateSecret(false);
    setReason('');
    setEditing(true);
  };

  let urlError: string | null = null;
  try {
    if (url) {
      const parsed = new URL(url);
      if (parsed.protocol !== 'https:') urlError = 'Webhook endpoints must be https.';
    }
  } catch {
    urlError = 'An absolute https URL.';
  }

  const save = useMutation({
    mutationFn: () => billingApi.putExtWebhook(env, tenantId, {
      url: url.trim(), events, enabled, rotate_secret: rotateSecret, reason: reason.trim(),
    }),
    gcTime: 0,
  });

  return (
    <Section title="Payment webhooks (ext)" meta="signed with X-Anyware-Signature — the secret is shown once"
      actions={canManage && webhook.data && <button type="button" className="btn btn-ghost btn-sm" onClick={startEdit}>{webhook.data.endpoint ? 'Edit' : 'Register'}</button>}>
      <QueryState query={webhook} service="billing">
        {(data) => data.endpoint ? (
          <div className="stack-sm">
            <div className="mono t-body" style={{ overflowWrap: 'anywhere' }}>{data.endpoint.url}</div>
            <div className="row">
              <StatusChip status={data.endpoint.enabled ? 'enabled' : 'disabled'} />
              <StatusChip status={data.endpoint.secret_set ? 'secret set' : 'no secret'} tone={data.endpoint.secret_set ? 'success' : 'warning'} />
              <span className="t-caption muted">{data.endpoint.events.join(', ')}</span>
            </div>
            <span className="t-caption muted">updated {formatTimestamp(data.endpoint.updated_at)}{data.endpoint.rotated_at && ` · secret rotated ${formatTimestamp(data.endpoint.rotated_at)}`}</span>
          </div>
        ) : (
          <div className="empty-state t-body">No webhook endpoint registered.</div>
        )}
      </QueryState>

      {editing && (
        <Modal title="Payment webhook endpoint" onClose={save.isPending ? undefined : () => setEditing(false)} dismissible={!save.isPending}
          footer={
            <>
              <button type="button" className="btn btn-ghost" onClick={() => setEditing(false)} disabled={save.isPending}>Cancel</button>
              <button type="button" className="btn btn-primary"
                disabled={!url || !!urlError || events.length === 0 || !reasonOk(reason) || save.isPending}
                onClick={() => save.mutate(undefined, {
                  onSuccess: (result) => {
                    const signing = result.signing_secret;
                    save.reset();
                    setEditing(false);
                    queryClient.invalidateQueries({ queryKey: ['ops', 'billing', env, 'ext-webhook', tenantId] });
                    if (signing) setSecret(signing);
                  },
                })}>
                {save.isPending ? 'Saving…' : 'Save endpoint'}
              </button>
            </>
          }>
          <TextField label="URL" value={url} onChange={setUrl} mono required error={url ? urlError : null} placeholder="https://api.example.com/webhooks/anyware" />
          <ChoiceChips label="Events" options={EXT_WEBHOOK_EVENTS.map((e) => ({ value: e }))} selected={events}
            onToggle={(e) => setEvents(toggleIn(events, e))} />
          <CheckboxField label="Enabled" checked={enabled} onChange={setEnabled} />
          <CheckboxField label="Rotate the signing secret" checked={rotateSecret} onChange={setRotateSecret}
            hint="The new secret is shown once; the account must switch to it." />
          <ReasonField value={reason} onChange={setReason} />
          <MutationError error={save.error} service="billing" />
        </Modal>
      )}

      {secret && (
        <RevealOnce title="Webhook signing secret" what="signing secret" secret={secret} onDismiss={() => setSecret(null)}>
          <p className="t-caption muted" style={{ margin: 0 }}>
            Deliveries carry <span className="mono">X-Anyware-Signature: t=…,v1=HMAC-SHA256(secret, "t.body")</span>.
          </p>
        </RevealOnce>
      )}
    </Section>
  );
}
